import { useEffect, useState } from "react";
import type { AdventureChoice } from "@ai-rpg/protocol";
import { listAdventures } from "../api.ts";
import { LOADING } from "../words.ts";

interface Props {
  /** The character who would play it: decides which adventures suit, and which rules they are read under. */
  readonly level: number;
  readonly edition: "2014" | "2024";
  readonly chosen: AdventureChoice | null;
  readonly onChoose: (adventure: AdventureChoice | null) => void;
}

const levelsOf = (a: AdventureChoice) =>
  a.levels === null ? "—" : a.levels.start === a.levels.end ? `${a.levels.start}` : `${a.levels.start}–${a.levels.end}`;
/** An adventure suits a character who is within the levels it is written for. */
const suits = (a: AdventureChoice, level: number) =>
  a.levels !== null && level >= a.levels.start && level <= a.levels.end;

/**
 * The published adventures in the player's books, as a table in a frame
 * that scrolls: narrowed by name, and to those written for the
 * character's level unless the player asks to see them all.
 */
export function AdventurePicker({ level, edition, chosen, onChoose }: Props) {
  const [all, setAll] = useState<AdventureChoice[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [everyLevel, setEveryLevel] = useState(false);

  useEffect(() => {
    let stale = false;
    listAdventures(edition)
      .then((found) => !stale && setAll(found))
      .catch((err: unknown) => !stale && setProblem(err instanceof Error ? err.message : String(err)));
    return () => {
      stale = true;
    };
  }, [edition]);

  const term = q.trim().toLowerCase();
  const fitting = all?.filter((a) => suits(a, level)) ?? [];
  const shown = (everyLevel ? (all ?? []) : fitting).filter(
    (a) => !term || a.title.toLowerCase().includes(term) || (a.storyline ?? "").toLowerCase().includes(term),
  );

  return (
    <div className="field">
      <span className="field-label">
        Adventure
        {all && (
          <span className="section-aside figures">
            {" "}
            {shown.length} of {all.length}
          </span>
        )}
      </span>
      {problem && (
        <p className="problem" role="alert">
          {problem}
        </p>
      )}
      {all?.length === 0 && (
        <p className="field-note">
          None of the books you may use is an adventure. An admin decides which books an account has.
        </p>
      )}
      {all && all.length > 0 && (
        <>
          <div className="grid-filters grid-filters-two">
            <label className="field">
              <span className="field-label">Name</span>
              <input
                className="input"
                type="search"
                value={q}
                maxLength={60}
                autoComplete="off"
                placeholder="Any"
                onChange={(e) => setQ(e.target.value)}
              />
            </label>
            <fieldset className="field">
              <legend className="field-label">Written for</legend>
              <div className="segments">
                <label className="segment" data-on={!everyLevel}>
                  <input
                    type="radio"
                    name="adventure-levels"
                    checked={!everyLevel}
                    onChange={() => setEveryLevel(false)}
                  />
                  Level {level}
                </label>
                <label className="segment" data-on={everyLevel}>
                  <input
                    type="radio"
                    name="adventure-levels"
                    checked={everyLevel}
                    onChange={() => setEveryLevel(true)}
                  />
                  Any level
                </label>
              </div>
            </fieldset>
          </div>
          <div className="grid-frame" tabIndex={0} aria-label="Adventures to choose from">
            <table className="grid grid-pick">
              <thead>
                <tr>
                  <th>Adventure</th>
                  <th>Levels</th>
                  <th>From</th>
                  <th>
                    <span className="visually-hidden">Choose</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((a) => (
                  <tr
                    key={a.bookId}
                    className="grid-row-pick"
                    data-on={chosen?.bookId === a.bookId}
                    // The whole row chooses it; the word at the end is there for the keyboard and a screen reader.
                    onClick={() => onChoose(chosen?.bookId === a.bookId ? null : a)}
                  >
                    <th scope="row">{a.title}</th>
                    <td className="figures">{levelsOf(a)}</td>
                    <td className="grid-quiet">{a.storyline ?? "—"}</td>
                    <td className="grid-actions">
                      {a.ready && <span className="grid-quiet">ready</span>}
                      <button
                        type="button"
                        className="link"
                        aria-pressed={chosen?.bookId === a.bookId}
                        onClick={(e) => {
                          e.stopPropagation();
                          onChoose(chosen?.bookId === a.bookId ? null : a);
                        }}
                      >
                        {chosen?.bookId === a.bookId ? "Chosen" : "Choose"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {shown.length === 0 && (
              <p className="quiet grid-none">
                {everyLevel || fitting.length > 0
                  ? "No adventure by that name."
                  : `None of your adventures is written for level ${level}. Look at any level, or bring another character.`}
              </p>
            )}
          </div>
          <p className="field-note">
            {chosen
              ? chosen.ready
                ? `${chosen.title} has been played here before: it opens in under a minute.`
                : `${chosen.title} hasn't been played here yet. Its opening is read from the book and made ready first, which takes two or three minutes; after that it is kept.`
              : "The book is played a part at a time, with three companions written for your character."}
            {chosen && !suits(chosen, level) && chosen.levels && (
              <>
                {" "}
                It is written for{" "}
                {chosen.levels.start === chosen.levels.end
                  ? `level ${chosen.levels.start}`
                  : `levels ${levelsOf(chosen)}`}
                ; its fights aren't changed to suit a level {level} character.
              </>
            )}
          </p>
        </>
      )}
      {all === null && !problem && (
        <p className="quiet" role="status">
          {LOADING}
        </p>
      )}
    </div>
  );
}
